// Which inline-create control is expanded, as CLIENT state in the URL
// fragment (#expand=<element-id>).
//
// The fragment, not a query parameter, and that distinction is the point: a
// fragment is never sent to the server -- not by convention, by HTTP -- so the
// server cannot become a second writer for this state, and it cannot reach a
// fragment-cache key. It used to be ?expand=, the server rendered `hidden`
// classes from it, and reconciling the two writers took drift detection, a
// MutationObserver and a retry ladder.
//
// Shared by inline_create_controller and inline_assigned_job_create_controller
// -- one store, so one control's claim still displaces the other's, exactly as
// it did when they shared the query parameter.
//
// Shaped `#expand=<id>` rather than `#<id>` so the browser and Turbo's scroll
// restoration do not jump to a matching element id.
const EXPAND_FRAGMENT_PREFIX = "expand="

export function expandedIdFromHash() {
  const raw = window.location.hash.replace(/^#/, "")
  if (!raw.startsWith(EXPAND_FRAGMENT_PREFIX)) return null

  // A hand-edited or truncated fragment can carry a malformed percent-escape,
  // and decodeURIComponent throws on those. A fragment we cannot read is a
  // fragment that expands nothing -- not an exception that takes the whole
  // controller down with it.
  try {
    return decodeURIComponent(raw.slice(EXPAND_FRAGMENT_PREFIX.length))
  } catch {
    return null
  }
}

export function writeExpandedFragment(elementId, active) {
  if (!elementId) return

  const url = new URL(window.location.href)
  // The control's own legacy ?expand= goes at the same time -- left in place
  // it rides along on the next Turbo refresh. Somebody else's stays: the
  // parameter is not exclusively ours (live-query-state uses it too).
  if (url.searchParams.get("expand") === elementId) url.searchParams.delete("expand")
  url.hash = active ? `${EXPAND_FRAGMENT_PREFIX}${encodeURIComponent(elementId)}` : ""
  window.history.replaceState({}, "", url.toString())
}

// A bookmarked or shared ?expand=<this-id> URL still has to work, and it can
// arrive on ANY navigation -- not only the first load. Call from connect and
// from turbo:load/turbo:render handlers; claiming the parameter only when the
// value names this element keeps other consumers' parameters intact.
export function migrateLegacyExpandParam(elementId) {
  if (!elementId) return

  const url = new URL(window.location.href)
  if (url.searchParams.get("expand") !== elementId) return

  url.searchParams.delete("expand")
  if (!url.hash) url.hash = `${EXPAND_FRAGMENT_PREFIX}${encodeURIComponent(elementId)}`
  window.history.replaceState({}, "", url.toString())
}
